import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";

import { Button } from "components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { Meta } from "../../../../../@types/paginated";
import { IGamificationFilters } from "../../../../../@types/gamification";

interface DataTablePaginationProps {
  meta: Meta;
  filters: IGamificationFilters;
  updateFilter: (data: { key: string; value: unknown }) => void;
}

export function DataTablePagination({
  meta,
  filters,
  updateFilter,
}: DataTablePaginationProps) {
  return (
    <div className="flex flex-col gap-4 px-2 sm:flex-row sm:items-center sm:justify-between">
      <div className="text-sm text-muted-foreground">
        {meta.total} linha(s) encontrada(s).
      </div>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-6 lg:gap-8">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium">Linhas por página</p>
          <Select
            value={`${meta.perPage}`}
            onValueChange={(value) => {
              updateFilter({
                key: "meta",
                value: { 
                  ...filters.meta,
                  perPage: parseInt(value),
                  page: 1
                },
              });
            }}
          >
            <SelectTrigger className="h-8 w-[90px]">
              <SelectValue placeholder={meta.perPage} />
            </SelectTrigger>
            <SelectContent side="top">
              {[10, 20, 30, 40, 50].map((pageSize) => (
                <SelectItem key={pageSize} value={`${pageSize}`}>
                  {pageSize}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center justify-center text-sm font-medium sm:w-[100px]">
          Página {meta.currentPage} de {meta.lastPage}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className="hidden h-8 w-8 p-0 lg:flex"
            onClick={() => {
              updateFilter({
                key: "meta",
                value: { ...filters.meta, page: 1 },
              });
            }}
            disabled={meta.currentPage === 1}
          >
            <span className="sr-only">Ir para primeira página</span>
            <ChevronsLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            className="h-8 w-8 p-0"
            onClick={() => {
              updateFilter({
                key: "meta",
                value: { ...filters.meta, page: meta.currentPage - 1 },
              });
            }}
            disabled={meta.currentPage === 1}
          >
            <span className="sr-only">Ir para página anterior</span>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            className="h-8 w-8 p-0"
            onClick={() => {
              updateFilter({
                key: "meta",
                value: { ...filters.meta, page: meta.currentPage + 1 },
              });
            }}
            disabled={meta.currentPage === meta.lastPage}
          >
            <span className="sr-only">Ir para próxima página</span>
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            className="hidden h-8 w-8 p-0 lg:flex"
            onClick={() => {
              updateFilter({
                key: "meta",
                value: { ...filters.meta, page: meta.lastPage },
              });
            }}
            disabled={meta.currentPage === meta.lastPage}
          >
            <span className="sr-only">Ir para última página</span>
            <ChevronsRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
